/**
 * Color helpers. The buy/sell pair is blue/orange (colorblind-safe, no
 * red/green dependency). Values are CSS variables so they follow the theme;
 * `cssVar()` resolves them to concrete strings for canvas-based charts.
 */
import type { Kind, Source, Tier } from "@/api/types";

export const SIDE = {
  buy: "var(--buy)",
  sell: "var(--sell)",
  buySoft: "var(--buy-soft)",
  sellSoft: "var(--sell-soft)",
  neutral: "var(--neutral)",
} as const;

export const KIND_COLOR: Record<Kind, string> = {
  buy: "var(--buy)",
  sell: "var(--sell)",
  send: "var(--transfer)",
  receive: "var(--transfer)",
  mint: "var(--mint)",
  burn: "var(--burn)",
  add_liquidity: "var(--liquidity)",
  remove_liquidity: "var(--liquidity)",
  collect_fees: "var(--liquidity)",
};

export const SOURCE_COLOR: Record<Source, string> = {
  distributor: "var(--chart-3)",
  market: "var(--chart-1)",
  liquidity: "var(--chart-6)",
};

export const RESULT_COLOR = {
  profit: "var(--ok)",
  underwater: "var(--danger)",
  unknown: "var(--neutral)",
} as const;

/** Categorical palette (8 steps) for venues, hooks, cohorts. */
export const CATEGORICAL = Array.from({ length: 8 }, (_, i) => `var(--chart-${i + 1})`);

/** Sequential ramp (6 steps, `--seq-0` empty … `--seq-5` most) for density charts. */
export const SEQ = Array.from({ length: 6 }, (_, i) => `var(--seq-${i})`);

export function categorical(i: number): string {
  return CATEGORICAL[i % CATEGORICAL.length]!;
}

/** Resolve `var(--x)` (or a bare `--x`) to its computed value. Canvas libraries need real colors. */
export function cssVar(
  nameOrVar: string,
  el: Element | null = typeof document !== "undefined" ? document.documentElement : null,
): string {
  if (!el) return nameOrVar;
  const m = /^var\((--[\w-]+)\)$/.exec(nameOrVar.trim());
  const name = m ? m[1]! : nameOrVar.startsWith("--") ? nameOrVar : null;
  if (!name) return nameOrVar;
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || nameOrVar;
}

/** Concrete theme palette for chart libraries, resolved from the current CSS variables. */
export interface ChartPalette {
  bg: string;
  fg: string;
  muted: string;
  grid: string;
  border: string;
  crosshair: string;
  buy: string;
  sell: string;
  liquidity: string;
  accent: string;
  series: string[];
  /** sequential ramp `--seq-0` (empty) … `--seq-5` (most), for density charts */
  seq: string[];
}

export function chartPalette(): ChartPalette {
  return {
    bg: cssVar("--bg-elev"),
    fg: cssVar("--fg"),
    muted: cssVar("--fg-muted"),
    grid: cssVar("--grid"),
    border: cssVar("--border"),
    crosshair: cssVar("--crosshair"),
    buy: cssVar("--buy"),
    sell: cssVar("--sell"),
    liquidity: cssVar("--liquidity"),
    accent: cssVar("--accent"),
    series: CATEGORICAL.map((c) => cssVar(c)),
    seq: SEQ.map((c) => cssVar(c)),
  };
}

export const TIER_ORDER: Tier[] = ["none", "small", "big", "whale"];
